import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { NATIVE_METHOD_PAYMENT_TYPE } from '../../components/pages/PaymentPage/NativePaymentButton';
import PaymentApi from '../../service/Api/paymentApi';

const initialState = {
    payments: {
        success: true,
        error: null,
        loading: false,
        plansList: [],
        productsList: []
    },
    history: {
        success: true,
        error: null,
        loading: false,
        transactionsList: [],
        hasMore: true,
        currentPage: 1,
        reloading: false
    },
    currentPaymentData: {}
};

export const fetchPlans = createAsyncThunk('payment/fetchPlan', async (params, { rejectedWithValue }) => {
    try {
        const data = await PaymentApi.getPlansList();
        const plans = data.data;
        plans.data.map((plan) => ({
            ...plan,
            isOffer: !!plan.basePrice
        }));
        return plans;
    } catch (error) {
        return rejectedWithValue(error);
    }
});

export const fetchProducts = createAsyncThunk('payment/fetchProduct', async (params, { rejectedWithValue }) => {
    try {
        const data = await PaymentApi.getProductsList();
        return data.data;
    } catch (error) {
        return rejectedWithValue(error);
    }
});

export const fetchHistory = createAsyncThunk('payment/fetchHistory', async (params, { rejectedWithValue }) => {
    try {
        const data = await PaymentApi.getPaymentsHistory(params.page);
        return data.data;
    } catch (error) {
        return rejectedWithValue(error);
    }
});

export const createTrial = createAsyncThunk('payment/createTrial', async (params, { rejectedWithValue }) => {
    try {
        const data = await PaymentApi.createPaymentTrial(params);
        return data.data;
    } catch (error) {
        return rejectedWithValue(error);
    }
});

export const createPlan = createAsyncThunk('payment/createPlan', async (params, { rejectedWithValue }) => {
    try {
        const data = await PaymentApi.createPaymentPlan(params.id, params.data);
        return data.data;
    } catch (error) {
        return rejectedWithValue(error);
    }
});

export const createProduct = createAsyncThunk('payment/createProduct', async (params, { rejectedWithValue }) => {
    try {
        const data = await PaymentApi.createPaymentProduct(params.id, params.data);
        return data.data;
    } catch (error) {
        return rejectedWithValue(error);
    }
});

export const createNativeMethodToken = createAsyncThunk('payment/createGooglePayToken', async (params, { rejectedWithValue }) => {
    try {
        const data =
            params.type === NATIVE_METHOD_PAYMENT_TYPE.GOOGLE
                ? await PaymentApi.createGooglePayToken(params.token)
                : await PaymentApi.createApplePayToken(params.token);
        return data.data;
    } catch (error) {
        return rejectedWithValue(error);
    }
});

export const cancelSubscription = createAsyncThunk('payment/cancelSubscription', async (params, { rejectedWithValue }) => {
    try {
        const data = await PaymentApi.cancelSubscription(params);
        return data.data;
    } catch (error) {
        return rejectedWithValue(error);
    }
});

export const getPaymentsState = (store) => store.payments.payments;
export const getPaymentsLoadingState = (store) => store.payments.payments.loading;

export const getTrialPlan = (store) => store.payments.payments?.plansList.find((plan) => plan.isTrial);
export const getSubscriptionPlan = (store) => store.payments.payments?.plansList.find((plan) => !plan.isTrial);

export const getPaymentsHistoryState = (store) => store.payments.history;
export const getHistoryLoadingState = (store) => store.payments.history.loading;
export const getTransactionsList = (store) => store.payments.history.transactionsList;

export const getCurrentPaymentData = (store) => store.payments.currentPaymentData;

const paymentsSlice = createSlice({
    name: 'payments',
    initialState,
    reducers: {
        clearPaymentsError(store) {
            store.payments.error = null;
        },
        clearHistoryError(store) {
            store.history.error = null;
        },
        setCurrentPaymentData(store, { payload }) {
            store.currentPaymentData = {
                ...payload
            };
        },
        setSubscriptionPlan(store, { payload }) {
            store.payments.plansList = store.payments.plansList.map((plan) => {
                return plan.isTrial ? plan : { ...plan, ...payload };
            });
        }
    },
    extraReducers(builder) {
        builder
            .addCase(fetchPlans.pending, (store) => {
                store.payments = {
                    ...store.payments,
                    loading: true,
                    error: null
                };
            })
            .addCase(fetchPlans.fulfilled, (store, { payload }) => {
                store.payments = {
                    ...store.payments,
                    loading: false,
                    success: payload?.success,
                    plansList: payload?.data
                };
            })
            .addCase(fetchPlans.rejected, (store, { payload }) => {
                store.payments = {
                    ...store.payments,
                    loading: false,
                    success: payload?.success,
                    error: payload?.error
                };
            })
            .addCase(fetchProducts.pending, (store) => {
                store.payments = {
                    ...store.payments,
                    loading: true,
                    error: null
                };
            })
            .addCase(fetchProducts.fulfilled, (store, { payload }) => {
                store.payments = {
                    ...store.payments,
                    loading: false,
                    success: payload?.success,
                    productsList: payload?.data
                };
            })
            .addCase(fetchProducts.rejected, (store, { payload }) => {
                store.payments = {
                    ...store.payments,
                    loading: false,
                    success: payload?.success,
                    error: payload?.error
                };
            })
            .addCase(fetchHistory.pending, (store, { meta: { arg } }) => {
                const reloading = typeof arg?.reloading === 'undefined' ? true : arg.reloading;
                store.history = {
                    ...store.history,
                    loading: true,
                    error: null,
                    reloading
                };
            })
            .addCase(fetchHistory.fulfilled, (store, { payload }) => {
                const reloading = store.history.reloading;
                store.history = {
                    ...store.history,
                    loading: false,
                    reloading: false,
                    success: payload?.success,
                    transactionsList: reloading ? [...payload.transaction] : [...store.history.transactionsList, ...payload.transaction],
                    hasMore: payload?.currentPage < payload?.allPages,
                    currentPage: Number.parseInt(payload?.currentPage, 10) + 1
                };
            })
            .addCase(fetchHistory.rejected, (store, { payload }) => {
                store.history = {
                    ...store.history,
                    loading: false,
                    reloading: false,
                    success: payload?.success,
                    error: payload?.error
                };
            })
            .addCase(createTrial.pending, (store) => {
                store.payments = {
                    ...store.payments,
                    loading: true,
                    error: null
                };
            })
            .addCase(createTrial.fulfilled, (store, { payload }) => {
                store.payments = {
                    ...store.payments,
                    loading: false,
                    success: payload?.success
                };
            })
            .addCase(createTrial.rejected, (store, { payload }) => {
                store.payments = {
                    ...store.payments,
                    loading: false,
                    success: payload?.success,
                    error: payload?.error
                };
            })
            .addCase(createPlan.pending, (store) => {
                store.payments = {
                    ...store.payments,
                    loading: true,
                    error: null
                };
            })
            .addCase(createPlan.fulfilled, (store, { payload }) => {
                store.payments = {
                    ...store.payments,
                    loading: false,
                    success: payload?.success
                };
            })
            .addCase(createPlan.rejected, (store, { payload }) => {
                store.payments = {
                    ...store.payments,
                    loading: false,
                    success: payload?.success,
                    error: payload?.error
                };
            })
            .addCase(createProduct.pending, (store) => {
                store.payments = {
                    ...store.payments,
                    loading: true,
                    error: null
                };
            })
            .addCase(createProduct.fulfilled, (store, { payload }) => {
                store.payments = {
                    ...store.payments,
                    loading: false,
                    success: payload?.success
                };
            })
            .addCase(createProduct.rejected, (store, { payload }) => {
                store.payments = {
                    ...store.payments,
                    loading: false,
                    success: payload?.success,
                    error: payload?.error
                };
            })
            .addCase(createNativeMethodToken.pending, (store) => {
                store.payments = {
                    ...store.payments,
                    loading: true,
                    error: null
                };
            })
            .addCase(createNativeMethodToken.fulfilled, (store, { payload }) => {
                store.payments = {
                    ...store.payments,
                    loading: false,
                    success: payload?.success
                };
            })
            .addCase(createNativeMethodToken.rejected, (store, { payload }) => {
                store.payments = {
                    ...store.payments,
                    loading: false,
                    success: payload?.success,
                    error: payload?.error
                };
            })
            .addCase(cancelSubscription.pending, (store) => {
                store.payments = {
                    ...store.payments,
                    loading: true,
                    error: null
                };
            })
            .addCase(cancelSubscription.fulfilled, (store, { payload }) => {
                store.payments = {
                    ...store.payments,
                    loading: false,
                    success: payload?.success
                };
            })
            .addCase(cancelSubscription.rejected, (store, { payload }) => {
                store.payments = {
                    ...store.payments,
                    loading: false,
                    success: payload?.success,
                    error: payload?.error
                };
            });
    }
});

export const paymentsReducer = paymentsSlice.reducer;

export const { clearPaymentsError, clearHistoryError, setCurrentPaymentData, setSubscriptionPlan } = paymentsSlice.actions;
